import { inject } from '@angular/core';
import { CanMatchFn, Route, Routes, UrlSegment } from '@angular/router';
import {
  AuthGuard,
  AuthService,
  NoAccessGuard,
  PermissionGuard,
  UserPreferenceStore,
} from '@ccfx/shared-lib';
import { AppPermission } from './features/shared/models/auth-profile.models';
import { UserPermissionStore } from './features/shared/services/user-permission.store';
import { REPORT_TAB_IDS } from './features/reports/report-tabs';
import { AP_TRANSACTION_TAB_IDS } from './features/transactions/transaction-tabs';

/**
 * Restricts a tab child route segment to only the explicitly listed tab values.
 * Any other value causes the route not to match, falling through to the
 * top-level `**` wildcard which redirects to /not-found.
 */
function allowedTabsGuard(validTabs: readonly string[], segmentIndex = 0): CanMatchFn {
  return (_route: Route, segments: UrlSegment[]): boolean => {
    const tab = segments[segmentIndex]?.path;
    return !!tab && validTabs.includes(tab);
  };
}

/**
 * Restricts the `partners/shipping-fees` tab to specific app roles.
 */
function partnersTabPermissionGuard(): CanMatchFn {
  return (_route: Route, segments: UrlSegment[]): boolean => {
    const tab = segments[0]?.path;
    if (!tab || tab !== 'shipping-fees') return true;

    const permissionStore = inject(UserPermissionStore);
    if (!permissionStore.loaded()) return true;

    return permissionStore.hasAnyAppPermission([
      AppPermission.VaultManager,
      AppPermission.OperationsAdmin,
    ]);
  };
}

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const authService = inject(AuthService);
      const userPreferenceStore = inject(UserPreferenceStore);

      if (!authService.isAuthenticated) {
        return '/login';
      }

      return `/${userPreferenceStore.defaultRoute() ?? 'dashboard'}`;
    },
  },
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./features/dashboard/components/dashboard/dashboard').then(
        (m) => m.DashboardComponent,
      ),
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: 'login',
    loadComponent: () => import('@ccfx/shared-lib/layout').then((m) => m.LoginComponent),
  },
  {
    path: 'rate-management',
    loadComponent: () =>
      import('./features/rate-management/components/rate-management/rate-management').then(
        (m) => m.RateManagement,
      ),
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
    children: [
      { path: '', redirectTo: 'view-rates', pathMatch: 'full' },
      {
        path: 'view-rates',
        loadComponent: () =>
          import('./features/rate-management/components/view-rates/view-rates').then(
            (m) => m.ViewRatesComponent,
          ),
      },
      {
        path: 'currencies',
        loadComponent: () =>
          import('./features/rate-management/components/currencies/currencies').then(
            (m) => m.Currencies,
          ),
      },
      {
        path: 'market-rates',
        loadComponent: () =>
          import('./features/rate-management/components/market-rates/market-rates').then(
            (m) => m.CcfxMarketRatesComponent,
          ),
      },
      {
        path: 'edit-tiers',
        loadComponent: () =>
          import('./features/rate-management/components/edit-tiers/edit-tiers').then(
            (m) => m.CcfxEditTiersComponent,
          ),
      },
      {
        path: 'scheduled',
        loadComponent: () =>
          import('./features/rate-management/components/scheduled-changes/scheduled-changes').then(
            (m) => m.CcfxScheduledChangesComponent,
          ),
      },
      {
        path: 'approvals',
        loadComponent: () =>
          import('./features/rate-management/components/tier-approvals/tier-approvals').then(
            (m) => m.CcfxTierApprovalsComponent,
          ),
      },
    ],
  },
  {
    path: 'vault-inventory',
    canMatch: [PermissionGuard],
    canActivate: [PermissionGuard],
    children: [
      { path: '', redirectTo: 'inventory', pathMatch: 'full' },
      {
        path: ':tab',
        canMatch: [allowedTabsGuard(['inventory', 'adjustments', 'holds', 'transactions'])],
        loadComponent: () =>
          import('./features/vault-inventory/components/vault-inventory/vault-inventory').then(
            (m) => m.VaultInventory,
          ),
      },
    ],
  },
  {
    path: 'user-preferences',
    canActivate: [PermissionGuard],
    children: [
      { path: '', redirectTo: 'general', pathMatch: 'full' },
      {
        path: ':tab',
        canMatch: [allowedTabsGuard(['general', 'system'])],
        loadComponent: () =>
          import('./features/user-preferences/components/default-route-selector/user-preferences').then(
            (m) => m.UserPreferences,
          ),
      },
    ],
  },
  {
    path: 'reports',
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
    children: [
      { path: '', redirectTo: 'shipping', pathMatch: 'full' },
      { path: 'ap-payments', redirectTo: '/transactions/ap-payments', pathMatch: 'full' },
      { path: 'ap-statement', redirectTo: '/transactions/ap-statement', pathMatch: 'full' },
      { path: 'ap-aging', redirectTo: '/transactions/ap-aging', pathMatch: 'full' },
      { path: 'ap-variance', redirectTo: '/transactions/ap-variance', pathMatch: 'full' },
      {
        path: ':tab',
        canMatch: [allowedTabsGuard(REPORT_TAB_IDS)],
        loadComponent: () =>
          import('./features/reports/reports').then((m) => m.ReportsComponent),
      },
    ],
  },
  {
    path: 'transactions',
    canMatch: [PermissionGuard],
    canActivate: [PermissionGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./features/transactions/components/transactions/transactions').then(
            (m) => m.Transactions,
          ),
      },
      {
        path: ':tab',
        canMatch: [allowedTabsGuard(AP_TRANSACTION_TAB_IDS)],
        loadComponent: () =>
          import('./features/transactions/components/transactions/transactions').then(
            (m) => m.Transactions,
          ),
      },
    ],
  },
  {
    path: 'ledger',
    loadComponent: () =>
      import('./features/ledger/components/ledger/ledger').then((m) => m.LedgerComponent),
    canMatch: [PermissionGuard],
    canActivate: [PermissionGuard],
  },
  {
    path: 'cod-outstanding',
    loadComponent: () =>
      import('./features/ledger/components/pod-outstanding/pod-outstanding').then(
        (m) => m.PodOutstandingComponent,
      ),
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: 'currencies',
    loadComponent: () =>
      import('./features/rate-management/components/currencies/currencies').then(
        (m) => m.Currencies,
      ),
    canMatch: [PermissionGuard],
    canActivate: [PermissionGuard],
  },
  {
    path: 'denominations',
    loadComponent: () =>
      import('./features/rate-management/components/denominations/denominations').then(
        (m) => m.Denominations,
      ),
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: 'scheduled-changes',
    loadComponent: () =>
      import('./features/rate-management/components/scheduled-changes/scheduled-changes').then(
        (m) => m.CcfxScheduledChangesComponent,
      ),
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: 'tier-approvals',
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
    children: [
      { path: '', redirectTo: 'pending', pathMatch: 'full' },
      {
        path: ':tab',
        canMatch: [allowedTabsGuard(['pending', 'history'])],
        loadComponent: () =>
          import('./features/rate-management/components/tier-approvals/tier-approvals').then(
            (m) => m.CcfxTierApprovalsComponent,
          ),
      },
    ],
  },

  {
    path: 'vault-management',
    loadComponent: () =>
      import('./features/vault-management/components/vault-management/vault-management').then(
        (m) => m.VaultManagementComponent,
      ),
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: 'supplier-restock',
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
    children: [
      { path: '', redirectTo: 'restock-orders', pathMatch: 'full' },
      {
        path: ':tab',
        canMatch: [allowedTabsGuard(['suppliers', 'restock-orders'])],
        loadComponent: () =>
          import('./features/supplier-restock/components/supplier-restock/supplier-restock').then(
            (m) => m.SupplierRestock,
          ),
      },
    ],
  },
  {
    path: 'partners',
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
    children: [
      { path: '', redirectTo: 'partners', pathMatch: 'full' },
      {
        path: 'applications/:applicationsSubTab',
        canMatch: [allowedTabsGuard(['applications']), allowedTabsGuard(['companies', 'users'], 1)],
        loadComponent: () =>
          import('./features/partners/components/partners/partners').then((m) => m.Partners),
      },
      {
        path: 'permissions/:permissionsSubTab',
        canMatch: [allowedTabsGuard(['permissions']), allowedTabsGuard(['company', 'users'], 1)],
        loadComponent: () =>
          import('./features/partners/components/partners/partners').then((m) => m.Partners),
      },
      {
        path: 'shipping-fees/:shippingFeesTab',
        canMatch: [
          allowedTabsGuard(['shipping-fees']),
          partnersTabPermissionGuard(),
          allowedTabsGuard(['global', 'fee-rules', 'company-overrides'], 1),
        ],
        loadComponent: () =>
          import('./features/partners/components/partners/partners').then((m) => m.Partners),
      },
      {
        path: ':tab',
        canMatch: [
          allowedTabsGuard([
            'partners',
            'users',
            'applications',
            'permissions',
            'shipping-fees',
            'valguard-config',
          ]),
          partnersTabPermissionGuard(),
        ],
        loadComponent: () =>
          import('./features/partners/components/partners/partners').then((m) => m.Partners),
      },
    ],
  },
  {
    path: 'order-management',
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
    children: [
      { path: '', redirectTo: 'inbox', pathMatch: 'full' },
      {
        path: ':tab',
        canMatch: [
          allowedTabsGuard([
            'inbox',
            'orders',
            'pending-approvals',
            'pending-orders',
            'pod-outstanding',
          ]),
        ],
        loadComponent: () =>
          import('./features/order-management/components/order-management/order-management').then(
            (m) => m.OrderManagement,
          ),
      },
    ],
  },
  {
    path: 'vault-fulfillment',
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
    children: [
      { path: '', redirectTo: 'packing', pathMatch: 'full' },
      {
        path: 'shipping/:shippingSubTab',
        canMatch: [allowedTabsGuard(['shipping']), allowedTabsGuard(['outgoing', 'incoming'], 1)],
        loadComponent: () =>
          import('./features/vault-fulfillment/components/vault-fulfillment/vault-fulfillment').then(
            (m) => m.VaultFulfillment,
          ),
      },
      {
        path: ':tab',
        canMatch: [allowedTabsGuard(['packing', 'counting', 'shipping', 'eod-reconciliation'])],
        loadComponent: () =>
          import('./features/vault-fulfillment/components/vault-fulfillment/vault-fulfillment').then(
            (m) => m.VaultFulfillment,
          ),
      },
    ],
  },
  {
    path: 'notification-management',
    loadComponent: () =>
      import('./features/notification-management/components/notification-management/notification-management').then(
        (m) => m.NotificationManagementComponent,
      ),
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: 'audit',
    loadComponent: () =>
      import('./features/audit/components/audit-log/audit-log').then((m) => m.AuditLogComponent),
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: 'email-admin',
    loadComponent: () =>
      import('./features/email-admin/components/email-admin-list/email-admin-list').then(
        (m) => m.EmailAdminListComponent,
      ),
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: 'compliance-dashboard',
    loadComponent: () =>
      import('./features/compliance-dashboard/components/compliance-dashboard/compliance-dashboard').then(
        (m) => m.ComplianceDashboardComponent,
      ),
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: 'kyc-review',
    canMatch: [PermissionGuard],
    canActivate: [AuthGuard, PermissionGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: ({ queryParams }) => (queryParams['tab'] === 'kyc' ? 'kyc' : 'document-expiry'),
      },
      {
        path: ':tab',
        canMatch: [allowedTabsGuard(['document-expiry', 'kyc'])],
        loadComponent: () =>
          import('./features/compliance-operations/components/compliance-operations/compliance-operations').then(
            (m) => m.ComplianceOperationsComponent,
          ),
      },
    ],
  },
  {
    path: 'document-expiry',
    redirectTo: '/kyc-review/document-expiry',
    pathMatch: 'full',
  },
  {
    path: 'kyc-compliance-queue',
    redirectTo: '/kyc-review/kyc',
    pathMatch: 'full',
  },
  {
    path: 'shipping-fees',
    redirectTo: '/partners/shipping-fees',
    pathMatch: 'full',
  },

  {
    path: 'no-access',
    loadComponent: () => import('@ccfx/shared-lib').then((m) => m.NoAccess),
    canActivate: [NoAccessGuard],
  },
  {
    path: 'not-found',
    loadComponent: () => import('./features/not-found/not-found').then((m) => m.NotFoundComponent),
  },
  {
    path: '**',
    redirectTo: 'not-found',
  },
];
